import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Code2, Edit3, Filter, MessageSquare, Plus, Search, Shield, User } from 'lucide-react';
import Header from '../layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { getAllFeedback, type FeedbackRecord, type FeedbackType } from '../services/feedbackService';
import { aikupData } from '../data/aikupData';
import { batirProFeedbackCardTitles } from '../data/batirProFeedbackCards';

const DEPARTMENT_LABELS: Record<string, string> = {
  'direction-ti': 'Direction TI (PRIsME BZ)',
  'batir-pro': 'Bâtir Pro (PRIsME Client)',
};

function formatDepartmentLabel(key: string): string {
  if (DEPARTMENT_LABELS[key]) return DEPARTMENT_LABELS[key];
  if (key === 'direction-ti') return 'Direction TI (PRIsME BZ)';
  const pretty = key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `PRIsME Client — ${pretty}`;
}
import FeedbackEditorModal from '../components/FeedbackEditorModal';
import PourLaProgModal from '../components/PourLaProgModal';

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Basse',
  2: 'Moyenne',
  3: 'Haute',
  4: 'Critique',
};

const TYPE_LABELS: Record<FeedbackType, string> = {
  BUG: 'Bug / anomalie',
  CONTENU: 'Contenu',
  SUGGESTION_CONCEPT: 'Suggestion de concept',
  LANGUE: 'Langue / coquille',
  UX: 'UX / expérience',
  BRAINSTORM: 'Brainstorm / idée brute',
  PROCESSUS_METHODOLOGIE: 'Processus / méthodologie',
  AUTRE: 'Autre',
};

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'slate' | 'blue' | 'orange' | 'emerald' | 'red';
}) {
  const map: Record<typeof tone, string> = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${map[tone]}`}>{children}</span>;
}

export default function CommentairesAmeliorationContinue() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const currentEmail = user?.email?.toLowerCase() || '';
  const isDany = currentEmail === 'dany.bourassa@bzinc.ca';
  const isProgUser = ['jean-michel.caouette@bzinc.ca', 'dany.bourassa@bzinc.ca'].includes(currentEmail);
  const isAdminUser = !!user && [
    'dany.bourassa@bzinc.ca',
    'jean-pierre.leclerc@bzinc.ca',
    'eric.hains@bzinc.ca',
    'francis.marquis@bzinc.ca',
    'jean-michel.caouette@bzinc.ca',
    'pierre-luc.fortin@bzinc.ca',
    'stephane.paradis@bzinc.ca',
    'francois.delisle@bzinc.ca',
    'dany.bourassa@bzinc.ca',
  ].includes(currentEmail);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<FeedbackRecord[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<FeedbackRecord | null>(null);
  const [selectedForProg, setSelectedForProg] = useState<FeedbackRecord | null>(null);

  // Filtres
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'ALL' | 'GENERAL' | 'DEPARTMENT' | 'CARD'>('ALL');
  const [type, setType] = useState<'ALL' | FeedbackType>('ALL');
  const [priority, setPriority] = useState<'ALL' | 1 | 2 | 3 | 4>('ALL');
  const [onlyWithoutPromisedDate, setOnlyWithoutPromisedDate] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<'ALL' | string>('ALL');

  useEffect(() => {
    if (!user) return;
    if (!isAdminUser) {
      navigate('/direction-ti');
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllFeedback({ includeCards: true });
        setItems(data || []);
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdminUser]);

  const cardTitleById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of aikupData) m.set(c.id, c.title);
    for (const c of batirProFeedbackCardTitles) m.set(c.id, c.title);
    return m;
  }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const byPriority = { 1: 0, 2: 0, 3: 0, 4: 0 } as Record<1 | 2 | 3 | 4, number>;
    let withoutPromised = 0;
    let cardCount = 0;
    for (const it of items) {
      const p = (it.priority ?? 2) as 1 | 2 | 3 | 4;
      if (byPriority[p] !== undefined) byPriority[p] += 1;
      if (!it.promised_date) withoutPromised += 1;
      if (it.context_level === 'CARD') cardCount += 1;
    }
    return { total, byPriority, withoutPromised, cardCount };
  }, [items]);

  const departmentOptions = useMemo(() => {
    const keys = Array.from(new Set(items.map((it) => it.department_key).filter(Boolean)));
    return keys.sort((a, b) => formatDepartmentLabel(a).localeCompare(formatDepartmentLabel(b), 'fr'));
  }, [items]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items
      .filter((it) => {
        if (departmentFilter !== 'ALL' && it.department_key !== departmentFilter) return false;
        if (scope !== 'ALL' && it.context_level !== scope) return false;
        if (type !== 'ALL' && it.type !== type) return false;
        if (priority !== 'ALL' && it.priority !== priority) return false;
        if (onlyWithoutPromisedDate && !!it.promised_date) return false;
        if (!query) return true;

        const cardTitle = it.card_id ? cardTitleById.get(it.card_id) : '';
        const haystack = [
          it.user_email,
          it.comment,
          it.department_key,
          it.context_level,
          it.type,
          String(it.priority ?? ''),
          cardTitle || '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
      .slice()
      .sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
  }, [items, q, departmentFilter, scope, type, priority, onlyWithoutPromisedDate, cardTitleById]);

  const grouped = useMemo(() => {
    const general: FeedbackRecord[] = [];
    const byCard = new Map<number, FeedbackRecord[]>();
    for (const it of filtered) {
      if (it.context_level === 'CARD' && it.card_id != null) {
        const arr = byCard.get(it.card_id) || [];
        arr.push(it);
        byCard.set(it.card_id, arr);
      } else {
        general.push(it);
      }
    }
    const cards = Array.from(byCard.entries())
      .map(([cardId, rows]) => ({
        cardId,
        cardTitle: cardTitleById.get(cardId) || `Carte #${cardId}`,
        rows,
      }))
      .sort((a, b) => a.cardTitle.localeCompare(b.cardTitle, 'fr'));
    return { general, cards };
  }, [filtered, cardTitleById]);

  if (!user) return null;
  if (!isAdminUser) return null;

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-50 to-gray-100">
      <Header
        title="Commentaires / Amélioration continue"
        subtitle="Vue globale : Direction TI (PRIsME BZ) + tous les PRIsME Client (Bâtir Pro et autres instances). Regroupé par cartes + filtres pour piloter le backlog."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <button
            onClick={() => navigate('/direction-ti')}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour Service & Direction TI
          </button>

          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Shield className="w-4 h-4 text-blue-600" />
            Accès admin PRISME uniquement
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
            <p className="text-xs text-gray-500">Total commentaires</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-1">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1">Département + cartes</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-orange-500">
            <p className="text-xs text-gray-500">Sans date promise</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-1">{stats.withoutPromised}</p>
            <p className="text-xs text-gray-500 mt-1">À planifier</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-indigo-500">
            <p className="text-xs text-gray-500">Commentaires sur cartes</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-1">{stats.cardCount}</p>
            <p className="text-xs text-gray-500 mt-1">Context_level = CARD</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-red-500">
            <p className="text-xs text-gray-500">Priorité haute + critique</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-1">{(stats.byPriority[3] || 0) + (stats.byPriority[4] || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">P3 + P4</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Filter className="w-4 h-4" />
              Filtres
            </div>
            <button
              onClick={() => {
                setEditorMode('create');
                setEditing(null);
                setEditorOpen(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-bz-blue px-4 py-2 text-sm font-extrabold text-white hover:bg-bz-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600">Recherche</label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Texte, email, carte…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-bz-blue focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Source</label>
              <select
                className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="ALL">Toutes les sources</option>
                {departmentOptions.map((key) => (
                  <option key={key} value={key}>
                    {formatDepartmentLabel(key)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Portée</label>
              <select className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" value={scope} onChange={(e) => setScope(e.target.value as any)}>
                <option value="ALL">Toutes</option>
                <option value="GENERAL">Général</option>
                <option value="DEPARTMENT">Département</option>
                <option value="CARD">Cartes</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Type</label>
              <select className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value as any)}>
                <option value="ALL">Tous</option>
                {(Object.keys(TYPE_LABELS) as FeedbackType[]).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Priorité</label>
              <select
                className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                value={priority}
                onChange={(e) => setPriority((e.target.value === 'ALL' ? 'ALL' : Number(e.target.value)) as any)}
              >
                <option value="ALL">Toutes</option>
                {[1, 2, 3, 4].map((p) => (
                  <option key={p} value={p}>
                    {p} – {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={onlyWithoutPromisedDate} onChange={(e) => setOnlyWithoutPromisedDate(e.target.checked)} />
              Afficher seulement ceux <strong>sans date promise</strong>
            </label>
            <div className="text-xs text-gray-500">{filtered.length} résultat(s)</div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bz-blue" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">{error}</div>
        ) : (
          <div className="space-y-6">
            {grouped.general.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-bz-blue" />
                    <h3 className="font-bold text-gray-900">Général & Département</h3>
                  </div>
                  <Pill tone="slate">{grouped.general.length}</Pill>
                </div>
                <div className="divide-y">
                  {grouped.general.map((fb) => (
                    <FeedbackRow
                      key={fb.id}
                      fb={fb}
                      cardTitle={undefined}
                      departmentLabel={formatDepartmentLabel(fb.department_key)}
                      currentEmail={currentEmail}
                      isDany={isDany}
                      isProgUser={isProgUser}
                      onEdit={(record) => {
                        setEditorMode('edit');
                        setEditing(record);
                        setEditorOpen(true);
                      }}
                      onOpenProg={setSelectedForProg}
                    />
                  ))}
                </div>
              </div>
            )}

            {grouped.cards.map((g) => (
              <div key={g.cardId} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b bg-linear-to-r from-sky-50 via-white to-white flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-sky-700 font-semibold">Carte</p>
                    <h3 className="font-bold text-gray-900 truncate">{g.cardTitle}</h3>
                  </div>
                  <Pill tone="blue">{g.rows.length}</Pill>
                </div>
                <div className="divide-y">
                  {g.rows.map((fb) => (
                    <FeedbackRow
                      key={fb.id}
                      fb={fb}
                      cardTitle={g.cardTitle}
                      departmentLabel={formatDepartmentLabel(fb.department_key)}
                      currentEmail={currentEmail}
                      isDany={isDany}
                      isProgUser={isProgUser}
                      onEdit={(record) => {
                        setEditorMode('edit');
                        setEditing(record);
                        setEditorOpen(true);
                      }}
                      onOpenProg={setSelectedForProg}
                    />
                  ))}
                </div>
              </div>
            ))}

            {grouped.general.length === 0 && grouped.cards.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center text-gray-600">
                Aucun commentaire ne correspond aux filtres.
              </div>
            )}
          </div>
        )}

        <FeedbackEditorModal
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          mode={editorMode}
          departmentKey={editing?.department_key || 'direction-ti'}
          currentEmail={currentEmail}
          isDany={isDany}
          initial={editing}
          onCreated={(created) => setItems((prev) => [created, ...prev])}
          onUpdated={(updated) => setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
        />
        {selectedForProg && (
          <PourLaProgModal
            key={selectedForProg.id}
            isOpen={!!selectedForProg}
            onClose={() => setSelectedForProg(null)}
            feedback={selectedForProg}
            onUpdated={(updated) => {
              setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
              setSelectedForProg(updated);
            }}
          />
        )}
      </div>
    </div>
  );
}

function FeedbackRow({
  fb,
  cardTitle,
  departmentLabel,
  currentEmail,
  isDany,
  isProgUser,
  onEdit,
  onOpenProg,
}: {
  fb: FeedbackRecord;
  cardTitle?: string;
  departmentLabel?: string;
  currentEmail: string;
  isDany: boolean;
  isProgUser: boolean;
  onEdit: (fb: FeedbackRecord) => void;
  onOpenProg?: (fb: FeedbackRecord) => void;
}) {
  const priorityTone: 'slate' | 'orange' | 'red' =
    fb.priority === 4 ? 'red' : fb.priority === 3 ? 'orange' : 'slate';

  const canEdit = isDany || (fb.user_email || '').toLowerCase() === currentEmail.toLowerCase();

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {departmentLabel && (
              <Pill tone={fb.department_key === 'direction-ti' ? 'slate' : 'orange'}>{departmentLabel}</Pill>
            )}
            <Pill tone="blue">{TYPE_LABELS[fb.type as FeedbackType] || fb.type}</Pill>
            <Pill tone={priorityTone}>
              {fb.priority} – {PRIORITY_LABELS[fb.priority] || `Priorité ${fb.priority}`}
            </Pill>
            {fb.context_level === 'CARD' && cardTitle && <Pill tone="emerald">Carte</Pill>}
          </div>
          <p className="text-sm text-gray-900 mt-2 whitespace-pre-wrap">{fb.comment}</p>
        </div>

        <div className="text-xs text-gray-500 flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            {fb.user_email}
          </span>
          {fb.created_at && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(fb.created_at).toLocaleDateString('fr-CA')}
            </span>
          )}
          {isProgUser && onOpenProg && (
            <button
              onClick={() => onOpenProg(fb)}
              className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors"
              title="Pour la prog"
            >
              <Code2 className="w-3.5 h-3.5" />
              Pour la prog
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onEdit(fb)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              title="Éditer"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Éditer
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap text-xs text-gray-600">
        <div className="flex items-center gap-3 flex-wrap">
          {fb.expected_date && <span>Souhaité: {new Date(fb.expected_date).toLocaleDateString('fr-CA')}</span>}
          {fb.promised_date && <span>Promis: {new Date(fb.promised_date).toLocaleDateString('fr-CA')}</span>}
          {fb.prog_status === 'termine' && (
            <Pill tone="emerald">Terminé</Pill>
          )}
          {fb.prog_status === 'en_cours' && (
            <Pill tone="blue">En cours</Pill>
          )}
          {fb.prog_date && (
            <span>Pour la prog: {new Date(fb.prog_date).toLocaleDateString('fr-CA')}</span>
          )}
          {!fb.promised_date && !fb.prog_status && <span className="text-orange-700">À planifier</span>}
        </div>
        <div className="flex items-center gap-2">
          {fb.approved_manager && <Pill tone="emerald">✔ Gestionnaire</Pill>}
          {fb.approved_ce && <Pill tone="emerald">✔ CE</Pill>}
          {fb.approved_dany && <Pill tone="emerald">✔ Dany</Pill>}
        </div>
      </div>
    </div>
  );
}

